import React from 'react';
import {view, Icon, type CssProperties, media, tokens} from '@sail/ui';
import type {IconAsset} from '@sail/icons/types';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {getStore} from 'src/v2/state/store';
import {urlRedirect} from '@stripe-internal/safe-links';
import {isEmbedded, isMobile} from 'src/utils/utils';
import {useSessionContext} from 'src/state/usePayinSessionContext';

import {message, useIntl} from '@sail/intl';

const messages = {
  redirect_cta: message<{
    businessName: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.redirect_screen.redirect_cta',
    description: 'Button to take customer back to merchant site',
    defaultMessage: 'Back to {businessName}',
  }),
};

type TerminalScreenProps = {
  title: React.ReactNode;
  icon?: IconAsset;
  iconCss?: CssProperties;
  subtitleMessage?: React.ReactNode;
  isSuccess: boolean;
  children?: React.ReactNode;
};

export const TerminalScreen: React.FC<TerminalScreenProps> = ({
  title,
  icon,
  iconCss,
  subtitleMessage,
  isSuccess,
  children,
}) => {
  const intl = useIntl();
  const store = getStore();
  const payinSessionContext = useSessionContext();
  const payinSessionReturnUrl = store.useSelector(
    (state) => state.paymentDetails.returnUrl,
  );
  const setupSessionReturnUrl = store.useSelector(
    (state) => state.mandateDetails.returnUrl,
  );
  const returnUrl =
    payinSessionContext.mode === 'pay'
      ? payinSessionReturnUrl
      : setupSessionReturnUrl;

  const businessName = store.useSelector(
    (state) => state.merchantBranding.businessName,
  );
  const isMobileDevice = isMobile();

  return (
    <view.div
      css={{
        stack: 'y',
        textAlign: 'center',
        gap: 'space.500',
        alignY: isMobileDevice ? 'center' : 'top',
        width: '380px',
        [media.down(tokens.viewport.medium)]: {
          paddingX: '16px',
          paddingY: '16px',
        },
      }}
    >
      <view.div css={{stack: 'y', alignItems: 'center', gap: 'space.150'}}>
        {icon ? (
          <Icon
            icon={icon}
            size="medium"
            css={{
              height: 'xxlarge',
              borderRadius: '50%',
              width: 'fill',
              ...iconCss,
            }}
          />
        ) : null}

        <Text
          type="title"
          css={{
            textAlign: 'center',
            alignItems: 'center',
            alignSelf: 'center',
          }}
        >
          {title}
        </Text>

        {subtitleMessage ? (
          <Text
            type="body"
            css={{
              textAlign: 'center',
              alignSelf: 'center',
            }}
          >
            {subtitleMessage}
          </Text>
        ) : null}
      </view.div>

      {children}

      {/*  The return_url from 3p is currently only used for successful checkout flows. */}
      {isSuccess && !isEmbedded() ? (
        <view.div css={{width: 'fill'}}>
          <BrandedButton onClick={() => urlRedirect(returnUrl)}>
            {intl.formatMessage(messages.redirect_cta, {businessName})}
          </BrandedButton>
        </view.div>
      ) : null}
    </view.div>
  );
};
